/**
 * 先画到半精度浮点缓冲，再做一次保色相的色调映射。
 *
 * 一天几百张照片时，星团在远处挤在几个像素里，加法混合会叠出远大于 1 的值。
 * 直接输出会冲成一片白；这里按最亮的通道整体压缩，越密越亮，但颜色还是星自己的颜色。
 *
 * 色调映射之前先加辉光（three.js 自带的 UnrealBloomPass，参数同原型），亮的星点、光晕、爱心会晕开。
 * 焦点虚化（P4c，design §11.11）：辉光之后从半精度缓冲做一条半分辨率的模糊链（逐级缩小再放大，dual filter），
 * 最后合成里按离焦点的远近混进去；星点不写深度，只能按屏幕距离近似景深。强度 0 时整条跳过。
 * 叠加层（照片面片、轮转光环里的卡片、选中星的圈）画在单独的缓冲上，色调映射之后才叠上来：
 * 不会像原型实测的那样被辉光晕成白块，也不会被色调映射压暗，但和星光一样跟着流体扭曲、色散（同原型 §4.4）。
 *
 * 最后合成也在这一步，和原型一样：流体速度场扭曲画面、沿流向的色散、染色光晕、暗角、颗粒，
 * 再加上入场用的白光（uFlash）和画面撕裂（uGlitch）。爆炸冲击波就是流体里的两圈推力（fluid.ts）。
 */
import * as THREE from 'three';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';

const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const FRAG = /* glsl */ `
uniform sampler2D tScene;
uniform sampler2D tVel;
uniform sampler2D tDye;
uniform sampler2D tCards;
uniform float uCards;
uniform float uExposure;
uniform float uTime;
uniform float uFlash;
uniform float uGlitch;
uniform float uDistort;
uniform float uAberr;
uniform float uDye;
uniform float uVignette;
uniform float uGrain;
uniform vec2 uRes;
uniform sampler2D tBlur;
uniform float uFocus;
uniform vec2 uFocusUv;
uniform vec2 uFocusR;
varying vec2 vUv;

float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }

void main() {
  // 流体把画面推开，推得越快色散越明显
  vec2 off = texture2D(tVel, vUv).xy * uDistort;
  float m = length(off);
  if (m > 0.05) {
    off *= 0.05 / m;
    m = 0.05;
  }
  vec2 dir = m > 1e-5 ? off / m : vec2(0.0);
  vec2 ab = dir * min(m * uAberr, 0.01);
  vec2 uv = vUv - off;
  if (uGlitch > 0.0) {
    // 画面崩溃：横向错位的条带 + RGB 分离
    float tq = floor(uTime * 24.0), r1 = hash(vec2(floor(vUv.y * 42.0), tq));
    uv.x += (r1 - 0.5) * 0.09 * uGlitch * step(0.62, r1);
    ab += vec2(0.014 * uGlitch, 0.0);
  }
  vec3 c = dot(ab, ab) > 0.0
    ? vec3(texture2D(tScene, uv - ab).r, texture2D(tScene, uv).g, texture2D(tScene, uv + ab).b)
    : texture2D(tScene, uv).rgb;
  if (uFocus > 0.0) {
    // 焦点虚化：离焦点远的地方换成模糊链化开的颜色。x 乘宽高比，距离和内外圈都是屏幕高度的比例（同下面的暗角）
    vec2 fd = uv - uFocusUv;
    fd.x *= uRes.x / uRes.y;
    float w = uFocus * smoothstep(uFocusR.x, uFocusR.y, length(fd));
    // 按 2.2 次方把留下的清楚图压下去：线性混合时强度 0.6 还剩四成清楚的星点，过了 sRGB 看着像六成多，
    // 星点照样一粒一粒的、不像化开；压过以后 0.6 看上去才像化开了六成
    w = 1.0 - pow(1.0 - w, 2.2);
    c = mix(c, texture2D(tBlur, uv).rgb, w);
  }
  c *= uExposure;
  // 加法混合万一溢出成无穷大或 NaN，按黑处理
  if (any(isnan(c)) || any(isinf(c))) c = vec3(0.0);
  c = max(c, vec3(0.0));
  float mx = max(max(c.r, c.g), c.b);
  // 0.6 以下原样，以上平滑地压到 1，斜率连续
  float k = 0.6;
  float mm = mx < k ? mx : k + (1.0 - k) * (1.0 - exp(-(mx - k) / (1.0 - k)));
  vec3 o = mx > 1e-5 ? c * (mm / mx) : c;
  // 特别密的地方稍微泛白，像星团的亮核
  o = mix(o, vec3(1.0), smoothstep(6.0, 40.0, mx) * 0.35);
  if (uCards > 0.0) {
    // 照片卡片（预乘透明度）叠在星光上面，用同一个扭曲过的坐标
    vec4 card = texture2D(tCards, uv);
    if (dot(ab, ab) > 0.0) {
      card.r = texture2D(tCards, uv - ab).r;
      card.b = texture2D(tCards, uv + ab).b;
    }
    o = o * (1.0 - card.a) + card.rgb;
  }
  o += texture2D(tDye, vUv).rgb * uDye;
  vec2 q = vUv - 0.5;
  q.x *= uRes.x / uRes.y;
  o *= 1.0 - uVignette * smoothstep(0.45, 1.25, length(q));
  o *= 1.0 + (hash(vUv * uRes + fract(uTime) * 100.0) - 0.5) * uGrain;
  o = clamp(o + uFlash * vec3(1.0, 0.92, 0.95), 0.0, 1.0);
  gl_FragColor = vec4(o, 1.0);
  #include <colorspace_fragment>
}
`;

/**
 * 模糊链（P4c）：dual filter。缩小时每级取中心和四个对角（各偏源图半个像素，借双线性），
 * 放大时 8 个点的帐篷形。全部半精度、不带深度：保住 HDR 亮度，亮星化开后过色调映射是柔和的光斑而不是灰雾；
 * 也不带 mip（Safari 往高级 mip 写很慢）。
 */
const DOWN_FRAG = /* glsl */ `
uniform sampler2D tSrc;
uniform vec2 uHalf;
varying vec2 vUv;
void main() {
  vec3 s = texture2D(tSrc, vUv).rgb * 4.0;
  s += texture2D(tSrc, vUv + vec2(-uHalf.x, -uHalf.y)).rgb;
  s += texture2D(tSrc, vUv + vec2(uHalf.x, -uHalf.y)).rgb;
  s += texture2D(tSrc, vUv + vec2(-uHalf.x, uHalf.y)).rgb;
  s += texture2D(tSrc, vUv + uHalf).rgb;
  gl_FragColor = vec4(s / 8.0, 1.0);
}
`;

const UP_FRAG = /* glsl */ `
uniform sampler2D tSrc;
uniform vec2 uHalf;
varying vec2 vUv;
void main() {
  vec3 s = vec3(0.0);
  s += texture2D(tSrc, vUv + vec2(-uHalf.x * 2.0, 0.0)).rgb;
  s += texture2D(tSrc, vUv + vec2(-uHalf.x, uHalf.y)).rgb * 2.0;
  s += texture2D(tSrc, vUv + vec2(0.0, uHalf.y * 2.0)).rgb;
  s += texture2D(tSrc, vUv + vec2(uHalf.x, uHalf.y)).rgb * 2.0;
  s += texture2D(tSrc, vUv + vec2(uHalf.x * 2.0, 0.0)).rgb;
  s += texture2D(tSrc, vUv + vec2(uHalf.x, -uHalf.y)).rgb * 2.0;
  s += texture2D(tSrc, vUv + vec2(0.0, -uHalf.y * 2.0)).rgb;
  s += texture2D(tSrc, vUv + vec2(-uHalf.x, -uHalf.y)).rgb * 2.0;
  gl_FragColor = vec4(s / 12.0, 1.0);
}
`;

/**
 * 模糊链的级数：缓冲高度到这个像素数（约 dpr 2 的 650 屏高、dpr 1.5 的 870）从 3 级升到 4 级（最低 1/16）。
 * 每多降一级，化开的半径在缓冲像素里大约翻倍，正好抵掉高像素比，让 CSS 像素里的半径在各档差不多（约 12–20）。
 */
const BLUR_TALL = 1300;

export class ToneMapPass {
  readonly target = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, depthBuffer: true });
  /** 辉光：亮度过阈值的部分模糊后加回半精度缓冲，再一起做色调映射 */
  readonly bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.9, 0.55, 0.2);
  /** 上一帧画场景本身用了几次绘制（不含辉光和最后合成） */
  sceneCalls = 0;
  /** 照片卡片的缓冲：第一次轮转时才建；sRGB 编码的 8 位，暗部不出色带 */
  private cardTarget: THREE.WebGLRenderTarget | null = null;
  /** 模糊链的缩小、放大目标（down[0] 是 1/2）：第一次有强度时才建；空数组是还没建 */
  private downRTs: THREE.WebGLRenderTarget[] = [];
  private upRTs: THREE.WebGLRenderTarget[] = [];
  private readonly size = new THREE.Vector2(1, 1);
  private readonly clearColor = new THREE.Color();
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  /** 全屏三角形：最后合成和模糊链的每一级都是它，换材质就行 */
  private readonly quad: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>;
  private readonly finalMat: THREE.ShaderMaterial;
  private readonly downMat = new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: DOWN_FRAG,
    uniforms: { tSrc: { value: null as THREE.Texture | null }, uHalf: { value: new THREE.Vector2() } },
    depthTest: false,
    depthWrite: false,
  });
  private readonly upMat = new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: UP_FRAG,
    uniforms: { tSrc: { value: null as THREE.Texture | null }, uHalf: { value: new THREE.Vector2() } },
    depthTest: false,
    depthWrite: false,
  });
  readonly uniforms = {
    tScene: { value: this.target.texture },
    tVel: { value: null as THREE.Texture | null },
    tDye: { value: null as THREE.Texture | null },
    tCards: { value: null as THREE.Texture | null },
    tBlur: { value: null as THREE.Texture | null },
    uCards: { value: 0 },
    uExposure: { value: 1 },
    uTime: { value: 0 },
    uFlash: { value: 0 },
    uGlitch: { value: 0 },
    // 原型的取值：速度 1000 时画面偏移约 0.2，封顶 0.05
    uDistort: { value: 0.0002 },
    uAberr: { value: 0.5 },
    uDye: { value: 0.32 },
    uVignette: { value: 0.55 },
    uGrain: { value: 0.07 },
    uRes: { value: new THREE.Vector2(1, 1) },
    // 焦点虚化（P4c）：强度 0 时模糊链整条跳过、最后合成也不采样
    uFocus: { value: 0 },
    uFocusUv: { value: new THREE.Vector2(0.5, 0.5) },
    uFocusR: { value: new THREE.Vector2(0.2, 0.55) },
  };

  constructor() {
    const g = new THREE.BufferGeometry();
    // 一个盖住全屏的大三角形
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    this.finalMat = new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, uniforms: this.uniforms, depthTest: false, depthWrite: false });
    this.quad = new THREE.Mesh(g, this.finalMat);
    this.quad.frustumCulled = false;
    this.scene.add(this.quad);
  }

  /** 传绘制缓冲的像素尺寸 */
  setSize(width: number, height: number): void {
    this.size.set(width, height);
    this.target.setSize(width, height);
    this.cardTarget?.setSize(width, height);
    // 模糊链第一次有强度时才建；建过了就跟着改尺寸（缓冲高度跨档时级数也会变）
    if (this.downRTs.length) this.layoutBlur();
    // 辉光内部从一半分辨率开始逐级缩小，和原型一样按绘制缓冲的像素尺寸算
    this.bloom.setSize(width, height);
    this.uniforms.uRes.value.set(width, Math.max(1, height));
  }

  /** 焦点虚化（P4c）：strength 是 0 整条跳过；x/y 是焦点的 uv，inner/outer 是屏幕高度比例的内外圈 */
  setFocus(f: { strength: number; x: number; y: number; inner: number; outer: number }): void {
    const u = this.uniforms;
    u.uFocus.value = f.strength;
    u.uFocusUv.value.set(f.x, f.y);
    u.uFocusR.value.set(f.inner, f.outer);
  }

  /** 建 / 调整模糊链：级数按缓冲高度定（BLUR_TALL），高度跨档时整条重建 */
  private layoutBlur(): void {
    const n = this.size.y >= BLUR_TALL ? 4 : 3;
    if (this.downRTs.length && this.downRTs.length !== n) {
      for (const rt of [...this.downRTs, ...this.upRTs]) rt.dispose();
      this.downRTs = [];
      this.upRTs = [];
    }
    for (let i = 0; i < n; i++) {
      // up[i] 和 down[i] 同一级、同一个尺寸：放大到 1/2 就停，输出是 up[0]
      const w = Math.max(1, Math.floor(this.size.x / 2 ** (i + 1))), h = Math.max(1, Math.floor(this.size.y / 2 ** (i + 1)));
      if (!this.downRTs[i]) this.downRTs[i] = new THREE.WebGLRenderTarget(w, h, { type: THREE.HalfFloatType, depthBuffer: false });
      else this.downRTs[i]!.setSize(w, h);
      if (i < n - 1) {
        if (!this.upRTs[i]) this.upRTs[i] = new THREE.WebGLRenderTarget(w, h, { type: THREE.HalfFloatType, depthBuffer: false });
        else this.upRTs[i]!.setSize(w, h);
      }
    }
  }

  /** this.target → 逐级缩小到 1/2^级数 再放大回 1/2；得到的半分辨率图交给最后合成的 tBlur */
  private blur(renderer: THREE.WebGLRenderer): void {
    this.layoutBlur();
    let src: THREE.Texture = this.target.texture, sw = this.size.x, sh = this.size.y;
    this.quad.material = this.downMat;
    for (const rt of this.downRTs) {
      this.downMat.uniforms.tSrc.value = src;
      this.downMat.uniforms.uHalf.value.set(0.5 / sw, 0.5 / sh);
      renderer.setRenderTarget(rt);
      renderer.render(this.scene, this.camera);
      src = rt.texture;
      sw = rt.width;
      sh = rt.height;
    }
    this.quad.material = this.upMat;
    for (let i = this.upRTs.length - 1; i >= 0; i--) {
      const rt = this.upRTs[i]!;
      this.upMat.uniforms.tSrc.value = src;
      this.upMat.uniforms.uHalf.value.set(0.5 / sw, 0.5 / sh);
      renderer.setRenderTarget(rt);
      renderer.render(this.scene, this.camera);
      src = rt.texture;
      sw = rt.width;
      sh = rt.height;
    }
    this.quad.material = this.finalMat;
    this.uniforms.tBlur.value = src;
  }

  /** cards：叠加层的场景（照片、轮转卡片），没有要画的就传 null */
  render(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, cards: THREE.Scene | null = null): void {
    renderer.setRenderTarget(this.target);
    renderer.render(scene, camera);
    this.sceneCalls = renderer.info.render.calls;
    if (this.bloom.strength > 0) this.bloom.render(renderer, this.target, this.target, 0, false);
    // 焦点虚化：强度 0 时整条跳过（画质关了辉光的档、没在看什么的时候都不到这里）
    if (this.uniforms.uFocus.value > 0) this.blur(renderer);
    if (cards) {
      if (!this.cardTarget) {
        this.cardTarget = new THREE.WebGLRenderTarget(this.size.x, this.size.y, { depthBuffer: false });
        this.cardTarget.texture.colorSpace = THREE.SRGBColorSpace;
      }
      const color = renderer.getClearColor(this.clearColor), alpha = renderer.getClearAlpha();
      renderer.setClearColor(0x000000, 0);
      renderer.setRenderTarget(this.cardTarget);
      renderer.render(cards, camera);
      renderer.setClearColor(color, alpha);
      this.uniforms.tCards.value = this.cardTarget.texture;
      this.uniforms.uCards.value = 1;
    } else this.uniforms.uCards.value = 0;
    renderer.setRenderTarget(null);
    renderer.render(this.scene, this.camera);
  }
}
